// GPS & Load — Development (2026-10-12). Per outfield player: 1 specialisation + 1 area to improve,
// for Training and for Match, over a fixed period, relative to his reference group (position →
// line → squad). Everything — blocks, reference groups, ratios, picks, sentences — comes from
// GET /api/gps/development/key-points (services/development/); this page only renders it.
// The Trends page is reached from here (header button), no longer from the GPS sub-tab bar.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TrendingUp, Download } from 'lucide-react';
import { getDevelopmentKeyPoints } from '../../api';
import { downloadBlob, renderReportPdf } from '../../pdfExport';
import DevelopmentPdf from './DevelopmentPdf';
import { useUrlParam } from '../../hooks/useUrlParam';
import { TRENDS_CSS } from './trendsShared';
import DevelopmentPlayerDrawer from './DevelopmentPlayerDrawer';
import {
  DEV_CSS, BLOCKS, BLOCK_LABEL, POSITION_ORDER, keyPointShort, keyPointCompact, stateShort, referenceBadge, referenceText, fmtDate,
  PCT_METRICS, matchPct, friendlyLabel,
} from './developmentShared';

function useIsNarrow(maxWidth = 899) {
  const query = `(max-width: ${maxWidth}px)`;
  const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const on = () => setNarrow(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, [query]);
  return narrow;
}

const th = { textAlign: 'left', padding: '6px 8px', fontSize: 10.5, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4, borderBottom: '1px solid var(--border)', verticalAlign: 'bottom' };
const thGroup = { ...th, fontSize: 11, color: 'var(--text-secondary)', textTransform: 'none', letterSpacing: 0, borderBottom: '2px solid var(--border-strong)', padding: '8px 8px 4px' };
const td = { padding: '7px 8px', borderBottom: '1px solid var(--border)', verticalAlign: 'top', fontSize: 12.5, color: 'var(--text-primary)', overflowWrap: 'anywhere' };
const tdPct = { ...td, padding: '7px 4px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };
const KINDS = ['specialisation', 'improvement'];
const KIND_LABEL = { specialisation: 'Specialisation', improvement: 'Area to improve' };
const COLUMNS = BLOCKS.flatMap(b => KINDS.map(kind => ({ block: b, kind })));
// Fixed layout: the 8 columns always fit the page width, no horizontal scroll. Widths in %
// (16 + 4 × 16.5 + 3 × 6 = 100) — the % of target columns only need room for "159%".
const COL_W = { player: 16, keyPoint: 16.5, pct: 6 };

const openKeys = onOpen => e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); } };

function PlayerName({ p }) {
  return (
    <div>
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{p.name}</div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
        {p.positionGroup}{p.positionGroup !== p.position ? ` (squad ${p.position})` : ''} · {p.minutesInWindow} min
      </div>
    </div>
  );
}

function Badge({ block }) {
  const b = referenceBadge(block);
  if (!b) return null;
  return <span className="dev-badge" title={`${b.title}. ${referenceText(block) || ''}`}>{b.label}</span>;
}

// One key-point cell's content (no wrapper). kind: 'specialisation' | 'improvement'.
function KeyPointText({ block, kind, withBadge }) {
  const kp = block.keyPoints[kind];
  const picked = kp && kp.kind !== 'none';
  return (
    <span title={kp?.text} style={{ color: picked ? 'var(--text-primary)' : 'var(--text-muted)', fontWeight: picked ? 600 : 400, fontVariantNumeric: 'tabular-nums' }}>
      {keyPointShort(kp)}
      {withBadge && <Badge block={block} />}
    </span>
  );
}

// Table form: metric + value on line 1, family (and LINE/SQUAD badge) on line 2.
function KeyPointCompact({ block, kind, withBadge }) {
  const kp = block.keyPoints[kind];
  const c = keyPointCompact(kp);
  if (!c) {
    return <span title={kp?.text} style={{ color: 'var(--text-muted)' }}>No standout{withBadge && <Badge block={block} />}</span>;
  }
  return (
    <div title={kp.text}>
      <div style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{c.main}</div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{c.family}{withBadge && <Badge block={block} />}</div>
    </div>
  );
}

// One "% of target" cell: muted with n when below the key-point minimum.
// Small "F" next to a % that includes an included friendly (legend in the summary bar).
const FriendlyMark = ({ title }) => (
  <sup title={title} style={{ fontSize: 9, fontWeight: 700, marginLeft: 1, color: 'var(--text-secondary)' }}>F</sup>
);

function PctCell({ block, metricKey, label, friendlyText }) {
  const v = matchPct(block, metricKey);
  const title = v.none ? `${label}: no valid match` : `${label}: ${v.text} of target on average over ${v.n} match${v.n === 1 ? '' : 'es'}${v.friendly ? `, incl. the ${friendlyText}` : ''}${v.few ? ' — below the 4 matches a key point needs' : ''}`;
  return (
    <td style={{ ...tdPct, color: v.none || v.few ? 'var(--text-muted)' : 'var(--text-primary)', fontWeight: v.none || v.few ? 400 : 600 }} title={title}>
      {v.text}{v.friendly && <FriendlyMark title={`incl. the ${friendlyText}`} />}
      {v.few && <div style={{ fontSize: 10, fontWeight: 400 }}>n={v.n}</div>}
    </td>
  );
}

function StateText({ blockKey, block }) {
  return (
    <span title={block.keyPoints.state?.text} style={{ color: 'var(--text-muted)' }}>
      — {stateShort(blockKey, block)}
    </span>
  );
}

const accent = (block, kind) => {
  const kp = block.keyPoints[kind];
  if (!kp || kp.kind === 'none') return 'dev-cell';
  return `dev-cell ${kind === 'specialisation' ? 'dev-spec' : 'dev-imp'}`;
};

function KeyPointsTable({ players, onOpen, friendlyText }) {
  let lastPos = null;
  return (
    <div>
      <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
        <colgroup>
          <col style={{ width: `${COL_W.player}%` }} />
          {COLUMNS.map(c => <col key={`${c.block}-${c.kind}`} style={{ width: `${COL_W.keyPoint}%` }} />)}
          {PCT_METRICS.map(m => <col key={m.key} style={{ width: `${COL_W.pct}%` }} />)}
        </colgroup>
        <thead>
          <tr>
            <th style={{ ...thGroup, borderBottom: 'none' }} />
            <th colSpan={2} style={thGroup}>Training <span style={{ fontWeight: 500, color: 'var(--text-muted)' }}>— vs position group</span></th>
            <th colSpan={2 + PCT_METRICS.length} style={thGroup}>Match <span style={{ fontWeight: 500, color: 'var(--text-muted)' }}>— vs position target</span></th>
          </tr>
          <tr>
            <th style={th}>Player</th>
            {COLUMNS.map(c => <th key={`${c.block}-${c.kind}`} style={th}>{KIND_LABEL[c.kind]}</th>)}
            {PCT_METRICS.map(m => <th key={m.key} style={{ ...th, padding: '6px 4px', textAlign: 'right' }} title={`Average ${m.label} as % of the position match target (as Match Performance's ${m.label}%)`}>{m.label}%</th>)}
          </tr>
        </thead>
        <tbody>
          {players.map(p => {
            const divider = p.positionGroup !== lastPos;
            lastPos = p.positionGroup;
            const open = () => onOpen(p.playerId);
            return (
              <React.Fragment key={p.playerId}>
                {divider && (
                  <tr><td colSpan={COLUMNS.length + 1 + PCT_METRICS.length} style={{ padding: '12px 8px 3px', fontSize: 10.5, fontWeight: 700, color: 'var(--text-dimmed)', letterSpacing: 0.4 }}>{p.positionGroup}</td></tr>
                )}
                <tr className="tr-row" tabIndex={0} onClick={open} onKeyDown={openKeys(open)} aria-label={`${p.name} — open details`}>
                  <td style={td}><PlayerName p={p} /></td>
                  {BLOCKS.map(b => (p[b].keyPoints.state
                    ? <td key={b} colSpan={2} style={td}><StateText blockKey={b} block={p[b]} /></td>
                    : ['specialisation', 'improvement'].map(kind => (
                      <td key={`${b}-${kind}`} style={td}>
                        <div className={accent(p[b], kind)}><KeyPointCompact block={p[b]} kind={kind} withBadge={kind === 'specialisation'} /></div>
                      </td>
                    ))))}
                  {PCT_METRICS.map(m => <PctCell key={m.key} block={p.match} metricKey={m.key} label={m.label} friendlyText={friendlyText} />)}
                </tr>
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// Cards: "% of target  TD 106% · HID 119% · SD 84%" (muted with n below the key-point minimum).
function MatchPctLine({ block, friendlyText }) {
  const vals = PCT_METRICS.map(m => ({ ...m, v: matchPct(block, m.key) }));
  if (vals.every(x => x.v.none)) return null;
  const few = vals.find(x => x.v.few);
  return (
    <div style={{ fontSize: 12, marginTop: 4, color: few ? 'var(--text-muted)' : 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums' }}>
      <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>% of target: </span>
      {vals.map(x => `${x.label} ${x.v.text}`).join(' · ')}
      {few && <span style={{ fontSize: 11 }}> (n = {few.v.n})</span>}
      {vals.some(x => x.v.friendly) && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}> · incl. {friendlyText}</span>}
    </div>
  );
}

function PlayerCard({ p, onOpen, friendlyText }) {
  const open = () => onOpen(p.playerId);
  return (
    <div className="tr-row" tabIndex={0} role="button" aria-label={`${p.name} — open details`} onClick={open} onKeyDown={openKeys(open)}
      style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, padding: 12 }}>
      <PlayerName p={p} />
      <div style={{ display: 'grid', gap: 8, marginTop: 10 }}>
        {BLOCKS.map(b => (
          <div key={b}>
            <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 3 }}>
              {BLOCK_LABEL[b]}{!p[b].keyPoints.state && <Badge block={p[b]} />}
            </div>
            {p[b].keyPoints.state ? (
              <div style={{ fontSize: 12.5 }}><StateText blockKey={b} block={p[b]} /></div>
            ) : (
              <div style={{ display: 'grid', gap: 4, fontSize: 12.5 }}>
                {['specialisation', 'improvement'].map(kind => (
                  <div key={kind} className={accent(p[b], kind)}>
                    <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>{kind === 'specialisation' ? 'Specialisation: ' : 'Area to improve: '}</span>
                    <KeyPointText block={p[b]} kind={kind} />
                  </div>
                ))}
              </div>
            )}
            {b === 'match' && <MatchPctLine block={p.match} friendlyText={friendlyText} />}
          </div>
        ))}
      </div>
    </div>
  );
}

function Skeleton() {
  return (
    <div aria-busy="true" aria-label="Loading key points" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: 14 }}>
      {Array.from({ length: 8 }, (_, i) => (
        <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
          <div className="tr-skel" style={{ width: 160, height: 30 }} />
          {COLUMNS.map(c => <div key={`${c.block}-${c.kind}`} className="tr-skel" style={{ flex: 1.4, height: 30 }} />)}
          {PCT_METRICS.map(m => <div key={m.key} className="tr-skel" style={{ flex: 1, height: 30 }} />)}
        </div>
      ))}
    </div>
  );
}

function Message({ title, body }) {
  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: '40px 20px', textAlign: 'center' }}>
      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>{title}</div>
      {body && <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 6 }}>{body}</div>}
    </div>
  );
}

const sortPlayers = list => [...list].sort((a, b) => {
  const pa = POSITION_ORDER.indexOf(a.positionGroup), pb = POSITION_ORDER.indexOf(b.positionGroup);
  return (pa < 0 ? 99 : pa) - (pb < 0 ? 99 : pb) || a.name.localeCompare(b.name);
});

// ── Page ───────────────────────────────────────────────────────────────────────────────────────
export default function Development({ user, onNavigate }) {
  const narrow = useIsNarrow();
  const [period, setPeriod] = useUrlParam('period', '');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [exporting, setExporting] = useState(false);
  const exportRef = useRef(null);
  const token = user?.sessionToken;

  // PDF export: mount the off-screen print version (DevelopmentPdf), capture it once it has
  // rendered, then unmount it again.
  useEffect(() => {
    if (!exporting || !exportRef.current || !data) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const buf = await renderReportPdf(exportRef.current, '#ffffff', { scale: 2 });
        if (cancelled) return;
        const p = data.meta.period;
        downloadBlob(new Blob([buf], { type: 'application/pdf' }), `development-${p.id}-${p.from}_to_${p.to}.pdf`);
      } catch (err) {
        console.error('PDF export failed:', err);
        alert('PDF export failed.');
      } finally {
        if (!cancelled) setExporting(false);
      }
    })();
    return () => { cancelled = true; };
  }, [exporting, data]);

  const load = useCallback(() => {
    if (!token) { setLoading(false); setError('session'); return Promise.resolve(); }
    setLoading(true); setError(null);
    return getDevelopmentKeyPoints(token, period)
      .then(d => setData(d))
      .catch(e => setError(String(e.message || e).includes('401') ? 'session' : 'load'))
      .finally(() => setLoading(false));
  }, [token, period]);

  useEffect(() => { load(); }, [load]);
  const closeDrawer = useCallback(() => setOpenId(null), []);

  const players = useMemo(() => sortPlayers(data?.players || []), [data]);
  const hasAnyData = players.some(p => BLOCKS.some(b => !p[b].keyPoints.state));
  const openPlayer = openId != null ? players.find(p => p.playerId === openId) : null;
  const meta = data?.meta;
  const periods = meta?.periods || [];
  const friendlyText = friendlyLabel(meta);

  return (
    <div className="trends-root dev-root" style={{ maxWidth: 1400, margin: '0 auto', paddingBottom: 40 }}>
      <style>{TRENDS_CSS}{DEV_CSS}</style>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 12 }}>
        <div style={{ minWidth: 0 }}>
          <h2 style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>
            Development{meta ? ` — ${meta.period.label} (${fmtDate(meta.period.from)} – ${fmtDate(meta.period.to)})` : ''}
          </h2>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
            Training vs position group · Match vs the position match target · provisional ±10% thresholds · a low value is not necessarily a weakness (role, tactics)
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {periods.length > 0 && (
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-secondary)' }}>
              Period
              <select value={period || meta?.period.id || ''} onChange={e => setPeriod(e.target.value)}
                style={{ padding: '5px 8px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: 12 }}>
                {periods.map(p => <option key={p.id} value={p.id}>{p.label} ({fmtDate(p.from)} – {fmtDate(p.to)})</option>)}
              </select>
            </label>
          )}
          <button type="button" className="dev-btn" onClick={() => setExporting(true)} disabled={!hasAnyData || loading || exporting}
            style={!hasAnyData || loading || exporting ? { opacity: 0.55, cursor: 'not-allowed' } : undefined}>
            <Download size={14} /> {exporting ? 'Exporting…' : 'PDF export'}
          </button>
          <button type="button" className="dev-btn" onClick={() => onNavigate && onNavigate('gps', 'trends')}>
            <TrendingUp size={14} /> Trends
          </button>
        </div>
      </div>

      {meta && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, padding: '9px 12px', marginBottom: 14, fontSize: 12, color: 'var(--text-secondary)' }}>
          <div>
            Match: {meta.matchesIncluded.filter(m => !m.friendly).length} competitive matches{friendlyText ? ` + ${friendlyText}` : ''}, 60+ min, % of position target
            {meta.matchesExcluded.length > 0 && <span style={{ color: 'var(--text-muted)' }}> ({meta.matchesExcluded.length} friendl{meta.matchesExcluded.length === 1 ? 'y' : 'ies'} left out)</span>}
            {' · '}Training: {meta.scenariosIncluded.length} scenarios (MD+1 → MD), training sessions only, at most 1 team session missed, vs squad median
            {meta.scenariosExcluded.length > 0 && (
              <span style={{ color: 'var(--text-muted)' }}> ({meta.scenariosExcluded.map(sc => `${sc.scenario} ${fmtDate(sc.start).slice(0, 5)} ${sc.reason === 'no_data' ? 'no data' : 'not complete'}`).join(', ')})</span>
            )}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            <span className="dev-cell dev-spec">above group / target</span>{'  '}<span className="dev-cell dev-imp" style={{ marginLeft: 10 }}>below group / target</span>
            {friendlyText && <span style={{ marginLeft: 10 }}><b>F</b> = incl. the {friendlyText}</span>}
          </div>
        </div>
      )}

      {error === 'session' ? (
        <Message title="Session expired" body="Log in again to view Development." />
      ) : error ? (
        <Message title="Key points could not be loaded" body={<button className="dev-btn" onClick={load} style={{ marginTop: 8 }}>Try again</button>} />
      ) : loading && !data ? (
        <Skeleton />
      ) : !hasAnyData ? (
        <Message title="No data for this period" body="No outfield player has enough matches or training scenarios in this period yet." />
      ) : (
        <div style={{ opacity: loading ? 0.6 : 1, transition: 'opacity 0.15s' }}>
          {narrow ? (
            <div style={{ display: 'grid', gap: 10 }}>{players.map(p => <PlayerCard key={p.playerId} p={p} onOpen={setOpenId} friendlyText={friendlyText} />)}</div>
          ) : (
            <section style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: 14 }}>
              <KeyPointsTable players={players} onOpen={setOpenId} friendlyText={friendlyText} />
            </section>
          )}
        </div>
      )}

      {exporting && data && (
        <div ref={exportRef} aria-hidden="true" style={{ position: 'fixed', top: 0, left: -10000, pointerEvents: 'none' }}>
          <DevelopmentPdf data={data} />
        </div>
      )}

      {openPlayer && <DevelopmentPlayerDrawer player={openPlayer} fullScreen={narrow} onClose={closeDrawer} />}
    </div>
  );
}
